import { apiTryFetchRetry } from '@editor/utils/apiFetchRetry';
import analytics from '@repo/analytics';

import {
  SEARCH_BACKGROUNDS,
  SEARCH_PHOTOS,
  SEARCH_TEXTURES,
} from '../global/analytics/events';
import { useUserStore } from '../stores/userStore';
import {
  BoardLayout,
  CmsFont,
  ElementGroup,
  ElementProps,
  GenericCmsResponse,
  Template,
  UnsplashPhoto,
} from '../types';

export const CMS_API_URL =
  process.env.REACT_APP_CMS_API_URL ||
  process.env.STORYBOOK_CMS_API_URL ||
  process.env.NEXT_PUBLIC_CMS_API_URL;

export type FreepikSearchResult = {
  id: number;
  title: string;
  name: string;
  imageUrl: string;
  groups: { name: string }[];
};

export interface ElementsPageInfo {
  elements: ElementProps[];
  skip: number;
  take: number;
  query?: string;
  total: number;
}

export interface ElementsOverviewInfo extends ElementsPageInfo {
  id: number;
  name: string;
  popularity: number;
  invertInDarkMode?: boolean;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createAPI = ({ url }: { url: string | undefined }) => {
  // gets the list of categories and groups in them
  const getOverview = async (): Promise<
    | {
        overview: {
          [key: string]: ElementsOverviewInfo[];
        };
      }
    | { success: false; error: string }
  > => {
    return await apiTryFetchRetry(`${url}/api/overview`);
  };

  // gets the list of categories and groups in them
  const getElementsOverview = async (
    sort = 'popularity'
  ): Promise<
    Record<string, ElementsOverviewInfo[]> | { success: false; error: string }
  > => {
    return await apiTryFetchRetry(`${url}/api/elements/overview?sort=${sort}`);
  };

  // gets the list of categories and groups in them
  const getTextureOverview = async (): Promise<
    | {
        overview: {
          [key: string]: ElementsOverviewInfo[];
        };
      }
    | { success: false; error: string }
  > => {
    return await apiTryFetchRetry(`${url}/api/textures/overview`);
  };

  const getBackgroundOverview = async (): Promise<
    | {
        overview: {
          [key: string]: ElementsOverviewInfo[];
        };
      }
    | { success: false; error: string }
  > => {
    return await apiTryFetchRetry(`${url}/api/backgrounds/overview`);
  };

  // get the elements of specified group
  const getGroupElements = async (
    groupId: string | number,
    take = 10,
    skip = 0,
    sort = 'name'
  ): Promise<
    | {
        error: string;
      }
    | {
        skip: number;
        take: number;
        elements: ElementProps[];
      }
  > => {
    return await apiTryFetchRetry(
      `${url}/api/groups/${groupId}?take=${take}&skip=${skip}&sort=${sort}`
    );
  };

  const getFreepikSearchElements = async (
    query: string,
    take = 10,
    skip = 0
  ): Promise<{
    success: boolean;
    hasMore?: boolean;
    cached?: boolean;
    results?: FreepikSearchResult[];
    error?: string;
  }> => {
    return await apiTryFetchRetry<
      GenericCmsResponse<{
        hasMore?: boolean;
        cached?: boolean;
        results?: FreepikSearchResult[];
      }>
    >(
      `${url}/api/freepik/search/?query=${encodeURIComponent(
        query
      )}&take=${take}&skip=${skip}`
    );
  };

  const getSearchTextures = async (
    query: string,
    take = 10,
    skip = 0,
    sort = 'name'
  ): Promise<ElementsPageInfo> => {
    const result = await apiTryFetchRetry<ElementsPageInfo>(
      `${url}/api/elements/search?query=${encodeURIComponent(
        query
      )}&take=${take}&skip=${skip}&isTexture=true&sort=${sort}`
    );

    if ('error' in result) {
      return { elements: [], total: 0, query, skip, take };
    }
    if (skip === 0) {
      analytics.track(SEARCH_TEXTURES, {
        label: query,
        value: result.total,
      });
    }

    return result;
  };

  const getSearchBackgrounds = async (
    query: string,
    take = 10,
    skip = 0,
    sort = 'name'
  ): Promise<ElementsPageInfo> => {
    const result = await apiTryFetchRetry<ElementsPageInfo>(
      `${url}/api/elements/search?query=${encodeURIComponent(
        query
      )}&take=${take}&skip=${skip}&isBackground=true&sort=${sort}`
    );

    if ('error' in result) {
      return { elements: [], total: 0, query, skip, take };
    }
    if (skip === 0) {
      analytics.track(SEARCH_BACKGROUNDS, {
        label: query,
        value: result.total,
      });
    }

    return result;
  };

  const getElementsSuggestions = async (): Promise<
    | {
        tags: string[];
        groups: string[];
      }
    | { success: false; error: string }
  > => {
    return await apiTryFetchRetry(`${url}/api/elements/suggestions`);
  };

  const getTextureSuggestions = async (): Promise<
    | {
        tags: string[];
        groups: string[];
      }
    | { success: false; error: string }
  > => {
    return await apiTryFetchRetry(
      `${url}/api/elements/suggestions?isTexture=true`
    );
  };

  const getBackgroundSuggestions = async (): Promise<
    | {
        tags: string[];
        groups: string[];
      }
    | { success: false; error: string }
  > => {
    return await apiTryFetchRetry(
      `${url}/api/elements/suggestions?isBackground=true`
    );
  };

  // get the fonts
  const getFonts = async (): Promise<
    GenericCmsResponse<{
      fonts: CmsFont[];
    }>
  > => {
    return await apiTryFetchRetry<
      GenericCmsResponse<{
        fonts: CmsFont[];
      }>
    >(`${url}/api/fonts/all`);
  };

  const updateBookmarks = async (
    templates: Template[]
  ): Promise<Template[]> => {
    const bookmarks = useUserStore.getState().bookmarks;
    if (bookmarks.length === 0) {
      return templates;
    }

    return templates?.map((template: Template) => ({
      ...template,
      bookmarked: !!bookmarks.find((id) => id === template.id),
    }));
  };

  // gets the list of template categories and groups in them

  // save the text layout, only supposed to be used by dev/design teams
  const __saveTextLayout = async (
    textLayout: {
      objects: unknown[];
      structure: unknown[];
    },
    preview: string
  ): Promise<{
    success: boolean;
    layout?: {
      id: string;
    };
  }> => {
    const resp = await apiTryFetchRetry<{
      success: boolean;
      layout?: {
        id: string;
      };
    }>(`${url}/api/layouts/create`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        layout: textLayout,
        preview,
      }),
    });
    if ('error' in resp) {
      return { success: false };
    }

    return resp;
  };

  // store the elements as ids, for which the use was registered
  const registeredElements: string[] = [];

  // register the use of an element
  const registerElementUse = async (id: string): Promise<boolean> => {
    // register the use of elements only once per session
    if (registeredElements.includes(id)) {
      return false;
    }

    registeredElements.push(id);
    try {
      const resp = await apiTryFetchRetry<void>(`${url}/api/elements/use`, {
        method: 'POST',
        body: JSON.stringify({ id }),
      });
      if (resp && 'error' in resp) {
        return false;
      }
    } catch (_error) {
      return false;
    }
    return true;
  };

  /**
   * TEXT LAYOUTS
   */

  // gets the list of text layouts groups in them
  const getTextLayoutsOverview = async (): Promise<
    | {
        layouts: {
          [key: string]: {
            layouts: BoardLayout[];
            id: number;
            name: string;
            skip: number;
            take: number;
            total: number;
          }[];
        };
      }
    | { success: false; error: string }
  > => {
    return await apiTryFetchRetry(`${url}/api/layouts/overview`);
  };

  // gets the text layouts of specified group
  const getGroupTextLayouts = async (
    groupId: string,
    take = 10,
    skip = 0,
    sort = 'name'
  ): Promise<
    | {
        group: ElementGroup;
        layouts: BoardLayout;
        skip: number;
        take: number;
      }
    | { success: false; error: string }
  > => {
    return await apiTryFetchRetry(
      `${url}/api/layouts/groups/${groupId}?take=${take}&skip=${skip}&sort=${sort}`
    );
  };

  const getTrendingPhotos = async (
    take = 10,
    skip = 0
  ): Promise<
    | {
        results: UnsplashPhoto[];
        skip: number;
        take: number;
        cached: boolean;
      }
    | { success: false; error: string }
  > => {
    return await apiTryFetchRetry(
      `${url}/api/unsplash/trending?take=${take}&skip=${skip}`
    );
  };

  const searchUnsplashPhotos = async (
    query: string,
    take = 10,
    skip = 0,
    disableAnalytics?: boolean
  ): Promise<{
    total?: number;
    total_pages?: number;
    cached?: boolean;
    results: UnsplashPhoto[];
  }> => {
    // Remove punctuation from the query cause unsplash api doesn't support it
    const queryWithoutPunctuation = query.replace(/[^\w\s]/g, '');

    const result = await apiTryFetchRetry<{
      total?: number;
      total_pages?: number;
      cached?: boolean;
      results: UnsplashPhoto[];
    }>(
      `${url}/api/unsplash/search/${encodeURIComponent(
        queryWithoutPunctuation
      )}?take=${take}&skip=${skip}`
    );
    if ('error' in result) {
      return { results: [] };
    }

    if (skip === 0 && !disableAnalytics) {
      analytics.track(SEARCH_PHOTOS, {
        label: queryWithoutPunctuation,
        value: result.total,
      });
    }

    return result;
  };

  const flagUnsplashDownload = async (
    id: string,
    downloadLocation: string
  ): Promise<
    | {
        success: boolean;
      }
    | {
        error: string;
      }
  > => {
    return await apiTryFetchRetry(`${url}/api/unsplash/download`, {
      method: 'POST',
      body: JSON.stringify({ id, downloadLocation }),
    });
  };

  // Get info of unsplash photo from unsplash via cms
  const getUnsplashPhotoInfo = async (
    id?: string
  ): Promise<{
    success: boolean;
    error?: string;
    result?: UnsplashPhoto;
  }> => {
    return await apiTryFetchRetry(`${url}/api/unsplash/photo/${id}`);
  };

  const fetchFreepikImage = async (
    id: number | string
  ): Promise<{
    success: boolean;
    url?: string;
    error?: string;
  }> => {
    return await apiTryFetchRetry(`${url}/api/freepik/download/${id}`);
  };

  // Get info of our element from cms
  const getElementInfo = async (
    objectName: string
  ): Promise<{ success: false; error: string } | ElementProps> => {
    return await apiTryFetchRetry(
      `${url}/api/elements/${encodeURIComponent(objectName)}`
    );
  };
  // Get info data of freepik icon
  const getFreepikIconInfo = async (
    id: string
  ): Promise<
    { success: false; error: string } | { result: FreepikSearchResult }
  > => {
    return await apiTryFetchRetry(
      `${url}/api/freepik/${encodeURIComponent(id)}`
    );
  };

  return {
    getOverview,
    getElementsOverview,
    getTextureOverview,
    getBackgroundOverview,
    getGroupElements,
    getFreepikSearchElements,
    getSearchTextures,
    getSearchBackgrounds,
    getElementsSuggestions,
    getTextureSuggestions,
    getBackgroundSuggestions,
    getFonts,
    __saveTextLayout,
    registerElementUse,
    getTextLayoutsOverview,
    getGroupTextLayouts,
    getTrendingPhotos,
    searchUnsplashPhotos,
    getUnsplashPhotoInfo,
    flagUnsplashDownload,
    fetchFreepikImage,
    updateBookmarks,
    getElementInfo,
    getFreepikIconInfo,
  };
};

export default createAPI({ url: CMS_API_URL });
